import { isEmpty } from '@fe-util/common';
import { Input, Select } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './index.less';

const { Option } = Select;

const DatePickerInput = ({
  className,
  style,
  disabled,
  value = '',
  onChange,
  linkage = true,
  allowClear = true,
  requiredType = 'day',
  suffix = '/',
  placeholder = {
    year: 'YYYY',
    month: 'MM',
    day: 'DD',
  },
  monthOptions = [
    { value: '01', label: '01' },
    { value: '02', label: '02' },
    { value: '03', label: '03' },
    { value: '04', label: '04' },
    { value: '05', label: '05' },
    { value: '06', label: '06' },
    { value: '07', label: '07' },
    { value: '08', label: '08' },
    { value: '09', label: '09' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' },
  ],
  yearOptions = [],
}) => {
  const [year, setYear] = useState('');
  const [yearSearch, setYearSearch] = useState('');
  const [yearOpen, setYearOpen] = useState(false);

  const [month, setMonth] = useState('');
  const [monthSearch, setMonthSearch] = useState('');
  const [monthOpen, setMonthOpen] = useState(false);

  const [day, setDay] = useState('');
  const [daySearch, setDaySearch] = useState('');
  const [dayOpen, setDayOpen] = useState(false);

  const yearRef = useRef(null);
  const yearSelectRef = useRef(null);

  const monthRef = useRef(null);
  const dayRef = useRef(null);

  // 年份选项 (1990-当前年份+5)
  const currentYear = new Date().getFullYear();
  const yearList =
    isEmpty(yearOptions) || yearOptions.length <= 0
      ? Array.from({ length: 100 }, (_, i) => ({
          value: currentYear + 5 - i,
          label: currentYear + 5 - i,
        }))
      : yearOptions;
  // 天数选项 (1-31，不考虑月份)
  const allDayOptions = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, '0'),
    label: (i + 1).toString().padStart(2, '0'),
  }));

  // 根据年份和月份计算天数
  const getDayOptions = (
    selectedYear,
    selectedMonth,
  )=> {
    if (!selectedYear || !selectedMonth) return [];

    const yearNum = parseInt(selectedYear);
    const monthNum = parseInt(selectedMonth);
    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => ({
      value: (i + 1).toString().padStart(2, '0'),
      label: (i + 1).toString().padStart(2, '0'),
    }));
  };

  const [dayOptions, setDayOptions] = useState([]);

  // 触发外部onChange
  const triggerChange = (y, m, d) => {
    if (requiredType === 'year') {
      if (y && m && d) {
        onChange?.(`${y}-${m}-${d}`);
      } else if (y && m) {
        onChange?.(`${y}-${m}`);
      } else if (y) {
        onChange?.(y);
      } else {
        onChange?.();
      }
    }
    if (requiredType === 'month') {
      if (y && m && d) {
        onChange?.(`${y}-${m}-${d}`);
      } else if (y && m) {
        onChange?.(`${y}-${m}`);
      } else {
        onChange?.();
      }
    }

    if (requiredType === 'day') {
      if (y && m && d) {
        onChange?.(`${y}-${m}-${d}`);
      } else {
        onChange?.();
      }
    }
  };

  /**
   * 返回true 表示没找到输入，验证不通过
   */
  const verifyYear = useCallback(
    (v) => {
      return yearList.filter((item) => item.value.toString() === v).length <= 0;
    },
    [yearList],
  );

  const verifyMonth = useCallback(
    (v) => {
      return (
        monthOptions.filter((item) => item.value.toString() === v).length <= 0
      );
    },
    [monthOptions],
  );

  const verifyDay = useCallback(
    (v) => {
      return (
        dayOptions.filter((item) => item.value.toString() === v).length <= 0
      );
    },
    [dayOptions],
  );

  // 解析初始值

  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split('-');
      setYear(y || '');
      setYearSearch(y || '');
      setMonth(m || '');
      setMonthSearch(m || '');
      setDay(d || '');
      setDaySearch(d || '');
      if (y && m && linkage) {
        setDayOptions(getDayOptions(y, m));
      } else {
        setDayOptions(allDayOptions);
      }
    }
  }, [value]);

  // 更新天数选项当月变化时

  useEffect(() => {
    if (linkage) {
      if (year && month) {
        const days = getDayOptions(year, month);

        setDayOptions(days);

        // 如果当前选择的day不在新月份的天数范围内，则重置day

        if (day && !days.some((d) => d.value === day)) {
          setDay('');
          setDaySearch('');

          triggerChange(year, month, '');
        }
      } else {
        setDayOptions([]);
      }
    } else {
      setDayOptions(allDayOptions);
    }
  }, [year, month, linkage]);

  const handleYearChange = (value) => {
    setYear(value);
    setYearSearch(value);
    triggerChange(value, month, day);

    // 年的时候，强制设置清空一下
    if (!isEmpty(value)) {
      // 如果输入了输入了4位错误的值，
      if (value.length >= 4 && verifyYear(value)) {
        // 清空值
        handleYearChange('');
      }
      setYearOpen(true);
    }
  };

  const handleMonthChange = (value) => {
    setMonth(value);
    setMonthSearch(value);
    triggerChange(year, value, day);
  };

  const handleDayChange = (value) => {
    setDay(value);
    setDaySearch(value);
    triggerChange(year, month, value);
  };

  const componentClass =`${className} iron-datePickerInput`;

  // useEffect(() => {
  //   /** 输入错值清空 */
  //   if (year && verifyYear(year)) {
  //     handleYearChange('');
  //   }
  // }, [year]);

  const clearYear = (value) => {
    /** 输入错值清空 */
    if (value && verifyYear(value)) {
      handleYearChange('');
      setYearOpen(false);
    }
  };

  const clearMonth = (value) => {
    /** 输入错值清空 */
    if (value && verifyMonth(value)) {
      handleMonthChange('');
      setMonthOpen(false);
    }
  };

  const clearDay = (value) => {
    /** 输入错值清空 */
    if (value && verifyDay(value)) {
      handleDayChange('');
      setDayOpen(false);
    }
  };

  return (
    <div className={componentClass} style={style}>
      <div className="iron-datePickerInput-item">
        <Input
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          status={undefined}
          allowClear={allowClear}
          variant="borderless"
          className="iron-datePickerInput-input"
          placeholder={placeholder?.year}
          value={year}
          disabled={disabled}
          maxLength={4}
          onChange={(e) => {
            const newValue = e.target.value.replace(/[^0-9]/g, '');
            handleYearChange(newValue);
          }}
          onPressEnter={(e) => {
            const value = e.currentTarget.value;
            if (monthRef.current && !verifyYear(value)) {
              setYearOpen(false);
              setMonthOpen(true);
              setMonthSearch('');
              //@ts-ignore
              monthRef.current.select();
            }
          }}
          onClick={() => {
            setYearSearch('');
            setYearOpen(true);
          }}
          onBlur={(e) => {
            const newValue = e.target.value;
            clearYear(newValue);
          }}
          //@ts-ignore
          ref={yearRef}
        />
        <Select
          open={yearOpen}
          placeholder={placeholder?.year}
          // value={year || undefined}
          searchValue={yearSearch}
          onDropdownVisibleChange={(visible) => {
            setYearOpen(visible);
            if (visible === false) {
              setYearSearch('');
              setMonthSearch('');
            }
          }}
          onSelect={(value) => {
            handleYearChange(value);
            setYearOpen(false);
            if (monthRef.current && !verifyYear(value)) {
              setMonthOpen(true);
              setMonthSearch('');
              //@ts-ignore
              monthRef.current.select();
            }
          }}
          //@ts-ignore
          ref={yearSelectRef}
          showSearch
          allowClear={allowClear}
          disabled={disabled}
          dropdownMatchSelectWidth={false}
          className="iron-datePickerInput-select"
          popupClassName="iron-datePickerInput-popup"
        >
          {yearList.map((y) => (
            <Option key={y.value} value={y.value.toString()}>
              {y.value}
            </Option>
          ))}
        </Select>
        <span className="iron-datePickerInput-suffix">{suffix}</span>
      </div>
      <div className="iron-datePickerInput-item">
        <Input
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          status={undefined}
          allowClear={allowClear}
          variant="borderless"
          className="iron-datePickerInput-input"
          placeholder={placeholder?.month}
          disabled={disabled}
          value={month}
          maxLength={2}
          onChange={(e) => {
            const newValue = e.target.value.replace(/[^0-9]/g, '');
            handleMonthChange(newValue);
          }}
          onClick={() => {
            setMonthOpen(true);
            setMonthSearch('');
          }}
          onPressEnter={(e) => {
            const value = e.currentTarget.value;
            if (dayRef.current && !verifyMonth(value)) {
              setMonthOpen(false);
              setDayOpen(true);
              setDaySearch('');
              //@ts-ignore
              dayRef.current.select();
            }
          }}
          onBlur={(e) => {
            const newValue = e.target.value;
            clearMonth(newValue);
          }}
          //@ts-ignore
          ref={monthRef}
        />
        <Select
          open={monthOpen}
          placeholder={placeholder?.month}
          // value={month || undefined}
          searchValue={monthSearch}
          onDropdownVisibleChange={(visible) => {
            setMonthOpen(visible);
            if (visible === false) {
              setMonthSearch('');
              setDaySearch('');
            }
          }}
          onSelect={(value) => {
            handleMonthChange(value);
            setMonthOpen(false);
            if (dayRef.current && !verifyMonth(value)) {
              setDayOpen(true);
              setDaySearch('');
              //@ts-ignore
              dayRef.current.select();
            }
          }}
          showSearch
          allowClear={allowClear}
          disabled={disabled}
          dropdownMatchSelectWidth={false}
          className="iron-datePickerInput-select"
          popupClassName="iron-datePickerInput-popup"
          // filterOption={(input: string, option: any) =>
          //   option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          // }
        >
          {monthOptions.map((m) => (
            <Option key={m.value} value={m.value.toString()}>
              {m.label}
            </Option>
          ))}
        </Select>
        <span className="iron-datePickerInput-suffix">{suffix}</span>
      </div>
      <div className="iron-datePickerInput-item">
        <Input
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          status={undefined}
          allowClear={allowClear}
          variant="borderless"
          className="iron-datePickerInput-input"
          placeholder={placeholder?.day}
          value={day}
          disabled={disabled ? disabled : (!year || !month) && linkage}
          maxLength={2}
          onChange={(e) => {
            const newValue = e.target.value.replace(/[^0-9]/g, '');
            handleDayChange(newValue);
          }}
          onClick={() => {
            setDayOpen(true);
            setDaySearch('');
          }}
          onPressEnter={(e) => {
            const value = e.currentTarget.value;
            if (dayRef.current && !verifyDay(value)) {
              setDayOpen(false);
              setDaySearch('');
              //@ts-ignore
              dayRef.current.select();
            }
          }}
          onBlur={(e) => {
            const newValue = e.target.value;
            clearDay(newValue);
          }}
          //@ts-ignore
          ref={dayRef}
        />
        <Select
          open={dayOpen}
          placeholder={placeholder?.day}
          // value={day || undefined}
          searchValue={daySearch}
          onDropdownVisibleChange={(visible) => {
            setDayOpen(visible);
            if (visible === false) {
              setDaySearch('');
            }
          }}
          onSelect={(value) => {
            handleDayChange(value);
            setDayOpen(false);
          }}
          disabled={disabled ? disabled : (!year || !month) && linkage}
          showSearch
          allowClear={allowClear}
          dropdownMatchSelectWidth={false}
          className="iron-datePickerInput-select"
          popupClassName="iron-datePickerInput-popup"
        >
          {dayOptions.map((d) => (
            <Option key={d.value} value={d.value.toString()}>
              {d.label}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

export default DatePickerInput;